import { toast } from 'react-hot-toast';

interface VersionInfo {
  version: string;
  timestamp: number;
}

class VersionService {
  private versionCheckInterval: number = 1 * 60 * 1000; // Check every 5 minutes
  private intervalId?: NodeJS.Timeout;
  private currentVersion?: VersionInfo;

  constructor() {
    // Get initial version on service creation
    this.fetchVersion();
  }

  async fetchVersion(): Promise<VersionInfo | null> {
    try {
      const url = '/version.json?' + new Date().getTime();
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Failed to fetch version');
      const versionInfo: VersionInfo = await response.json();
      return versionInfo;
    } catch (error) {
      console.error('Error fetching version:', error);
      return null;
    }
  }

  async checkVersion(): Promise<boolean> {
    const newVersion = await this.fetchVersion();
    if (!newVersion) return false;

    // If we don't have a current version, set it and return false (no update needed)
    if (!this.currentVersion) {
      this.currentVersion = newVersion;
      return false;
    }

    // Check if version has changed
    const hasUpdate = newVersion.version !== this.currentVersion.version;
    if (hasUpdate) {
      this.currentVersion = newVersion;
    }
    return hasUpdate;
  }

  startVersionCheck(onUpdateAvailable: () => void) {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Start periodic version check
    this.intervalId = setInterval(async () => {
      const hasUpdate = await this.checkVersion();
      if (hasUpdate) {
        onUpdateAvailable();
      }
    }, this.versionCheckInterval);

    // Also check immediately
    this.checkVersion().then(hasUpdate => {
      if (hasUpdate) {
        onUpdateAvailable();
      }
    });
  }

  stopVersionCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  clearCache() {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
  }

  async refreshApp() {
    try {
      // Clear cache
      this.clearCache();
      
      // Show loading toast
      toast.loading('Refreshing application...');
      
      // Force reload the page
      window.location.reload();
    } catch (error) {
      console.error('Error refreshing app:', error);
      toast.error('Failed to refresh application. Please try again.');
    }
  }
}

export const versionService = new VersionService(); 